<template>
  <div class="shipping-label-form-container">
    <AppRow :gap="24">
      <!-- Columna del formulario -->
      <AppCol cols="12" md="4" class="shipping-label-form__col" style="margin-bottom: 10px;">
        <AppCard title="Personaliza tu etiqueta" subtitle="Configura tu etiqueta de envío con tu logo y datos de contacto.">
          <AppFormGroup label="Logo de tu marca" for="store-logo">
            <AppInputFile
              id="store-logo"
              :value="logoFile"
              :disabled="saving"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              @input="onLogoChange"
            />
            <small v-if="store && store.labelLogoUrl && !logoFile" class="logo-current">
              Logo actual:
              <a :href="store.labelLogoUrl" target="_blank" rel="noopener">{{ store.labelLogoUrl }}</a>
            </small>
            <small v-else-if="store && store.labelLogoUrl" class="logo-current">
              Se subirá un nuevo logo al guardar.
            </small>
            <small class="form-helper">
              PNG, JPEG, WebP o SVG. Hasta 5 MB.
            </small>
          </AppFormGroup>

          <AppFormGroup label="Línea 1" for="custom-field-1">
            <AppInput
              id="custom-field-1"
              v-model="form.labelCustomField1"
              :disabled="saving"
              placeholder="Ej: @mi_tienda · Instagram"
            />
            <small class="form-helper">
              Ideal para tus redes sociales.
            </small>
          </AppFormGroup>

          <AppFormGroup label="Línea 2" for="custom-field-2">
            <AppInput
              id="custom-field-2"
              v-model="form.labelCustomField2"
              :disabled="saving"
              placeholder="Ej: WhatsApp +56 9 1111 2222"
            />
            <small class="form-helper">
              Canal de contacto o mensaje corto.
            </small>
          </AppFormGroup>

          <template #footer>
            <div class="form-actions">
              <AppButton
                variant="primary"
                outline
                :disabled="saving"
                @click="onReset"
              >Descartar</AppButton>
              <AppButton
                :loading="saving"
                :disabled="saving"
                @click="onSave"
              >Guardar</AppButton>
            </div>
          </template>
        </AppCard>
      </AppCol>

      <!-- Columna de preview -->
      <AppCol cols="12" md="8" class="shipping-label-form__col">
        <AppCard title="Vista previa de la etiqueta" subtitle="Así queda la etiqueta en cada paquete.">
          <div v-if="loading" class="preview-loading">
            <b-spinner variant="secondary" small /> Cargando datos del store...
          </div>
          <ShippingLabel
            v-else
            :store-logo="logoPreviewUrl"
            :qr-svg="previewQrSvg"
            store-name="Nombre de tienda"
            :custom-field1="form.labelCustomField1"
            :custom-field2="form.labelCustomField2"
            tracking-code="5563863"
            :recipient="previewRecipient"
          />
        </AppCard>
      </AppCol>
    </AppRow>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AppContainer from '@/components/UI/AppContainer'
import AppRow from '@/components/UI/AppRow'
import AppCol from '@/components/UI/AppCol'
import AppCard from '@/components/UI/AppCard'
import AppInput from '@/components/UI/AppInput'
import AppFormGroup from '@/components/UI/AppFormGroup'
import AppButton from '@/components/UI/AppButton'
import AppInputFile from '@/components/UI/AppInputFile.vue'
import ShippingLabel from '@/components/delivery/printPackages/ShippingLabel'
import { qrContentToSvgDataUri } from '@/utils/qrSvg'

/**
 * spec 003: formulario de configuración de la etiqueta de envío.
 *
 * Carga los datos reales del store desde el backend (vía `getStore`),
 * permite editar logo + custom fields, y al guardar llama a:
 *   - POST /store/label-settings/logo (multipart) si hay archivo nuevo
 *   - PUT /store/:id/label-settings (json) para los custom fields
 *
 * La preview usa los datos del store + un QR placeholder hardcodeado
 * para que se vea el layout sin depender de un delivery real.
 *
 * spec 006: se refactoriza para usar AppContainer/AppRow/AppCol/AppCard,
 * con layout responsive que colapsa a 1 columna en mobile (≤768px).
 */
export default {
  name: 'ShippingLabelForm',
  components: {
    AppContainer,
    AppRow,
    AppCol,
    AppCard,
    AppInput,
    AppFormGroup,
    AppButton,
    AppInputFile,
    ShippingLabel
  },
  data () {
    return {
      loading: true,
      saving: false,
      logoFile: null,
      logoPreviewUrl: '',
      store: null,
      form: {
        labelCustomField1: '',
        labelCustomField2: ''
      },
      previewQrContent: '968711,5007,1780424793,c0cddec0db73',
      previewQrSvg: null,
      previewRecipient: {
        name: 'Nombre del destinatario',
        address: 'Dirección completa',
        commune: 'Comuna',
        phone: '+56 9 0000 0000'
      }
    }
  },
  computed: {
    ...mapGetters(['user']),
    /**
     * Si el usuario es admin sin restricción, usa storeId del form.
     * Si es store, usa siempre su propio storeId (no puede editar otra tienda).
     * Fallback: $route.params.id si la vista es /store/:id.
     */
    effectiveStoreId () {
      if (this.user && this.user.role === 'store') return this.user.storeId
      return (this.$route && this.$route.params && this.$route.params.id) || null
    }
  },
  async mounted () {
    await this.loadStore()
    // Spec 008: el QR ahora es prop, no se autogenera. Resolvemos el del preview
    // (data dummy) una vez que el componente está montado. Si el contenido cambia
    // en el futuro, mover a un watch sobre `previewQrContent`.
    this.previewQrSvg = await qrContentToSvgDataUri(this.previewQrContent)
  },
  methods: {
    ...mapActions(['getStore', 'uploadLabelLogo', 'updateLabelSettings']),

    async loadStore () {
      this.loading = true
      try {
        const id = this.effectiveStoreId
        if (!id) {
          this.loading = false
          return
        }
        const response = await this.getStore({ id })
        // getStore devuelve la response de axios; .data es el store pelado.
        const store = response && response.data ? response.data : response
        if (store && store.id) {
          this.store = store
          this.form.labelCustomField1 = store.labelCustomField1 || ''
          this.form.labelCustomField2 = store.labelCustomField2 || ''
          this.logoPreviewUrl = store.labelLogoUrl || ''
        }
      } catch (err) {
        this.$bvToast.toast('No se pudieron cargar los datos de la tienda', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    onLogoChange (file) {
      this.logoFile = file || null
      if (file) {
        // Preview local inmediata con FileReader (sólo para la preview;
        // el upload real va por el endpoint multipart).
        const reader = new FileReader()
        reader.onload = (e) => {
          this.logoPreviewUrl = e.target.result
        }
        reader.readAsDataURL(file)
      } else {
        // Si el usuario limpió el input, vuelve al logo actual del backend.
        this.logoPreviewUrl = this.store && this.store.labelLogoUrl ? this.store.labelLogoUrl : ''
      }
    },

    async onSave () {
      const id = this.effectiveStoreId || (this.store && this.store.id)
      if (!id) {
        this.$bvToast.toast('No se puede guardar: falta el id de la tienda', {
          title: 'Error',
          variant: 'danger'
        })
        return
      }
      this.saving = true
      try {
        // 1) Subir logo si hay archivo nuevo.
        if (this.logoFile) {
          await this.uploadLabelLogo({ file: this.logoFile, storeId: id })
        }
        // 2) Actualizar custom fields (siempre — pueden haber cambiado aunque
        // el logo no haya cambiado).
        await this.updateLabelSettings({
          storeId: id,
          labelCustomField1: this.form.labelCustomField1,
          labelCustomField2: this.form.labelCustomField2
        })
        this.$bvToast.toast('Configuración de etiqueta guardada', {
          title: 'Éxito',
          variant: 'success'
        })
        // Resetear el logoFile para que el form quede limpio
        this.logoFile = null
        // Recargar el store para reflejar labelLogoUrl nuevo
        await this.loadStore()
      } catch (err) {
        const message = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'No se pudo guardar la configuración'
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.saving = false
      }
    },

    onReset () {
      this.logoFile = null
      if (this.store) {
        this.form.labelCustomField1 = this.store.labelCustomField1 || ''
        this.form.labelCustomField2 = this.store.labelCustomField2 || ''
        this.logoPreviewUrl = this.store.labelLogoUrl || ''
      } else {
        this.form.labelCustomField1 = ''
        this.form.labelCustomField2 = ''
        this.logoPreviewUrl = ''
      }
    }
  }
}
</script>

<style scoped>
.shipping-label-form-container {
  /* AppContainer provee max-width + padding + centrado */
}

/* En mobile: reducir gap y asegurar que los hijos no se desborden */
@media (max-width: 768px) {
  .shipping-label-form-container :deep(.app-row) {
    --app-row-gutter-x: 16px;
    --app-row-gutter-y: 16px;
  }
}

.preview-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2rem;
  color: #6c757d;
  justify-content: center;
}

.form-actions {
  justify-content: flex-end;
  display: flex;
  gap: 8px;
}

/* Mobile: botones full-width apilados, Guardar abajo */
@media (max-width: 768px) {
  .form-actions {
    flex-direction: column-reverse;
  }
  .form-actions :deep(.app-btn) {
    width: 100%;
  }
}

.logo-current {
  display: block;
  margin-top: 4px;
  color: #6c757d;
  font-size: 0.8rem;
  word-break: break-all;
}

.logo-current a {
  color: #007bff;
}

.form-helper {
  display: block;
  margin-top: 6px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #6c757d;
}
</style>
